import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import ORBMonitoring from './ORBMonitoring';
import TradingMonitoring from './TradingMonitoring';
import V1VWAPMonitoring from './V1VWAPMonitoring';
import EODView from './EODView';
import OptSpyDailyView from './OptSpyDailyView';
import GexMapView from './GexMapView';
import ShadowsView from './ShadowsView';
import LiveTradeTracker from './LiveTradeTracker';
import { useAuth } from '../context/AuthContext';
import { createChart, ColorType, CandlestickSeries, createSeriesMarkers } from 'lightweight-charts';

import {
  Box,
  Tabs,
  Tab,
  Typography,
  Paper,
  Chip,
  TextField,
  Button,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import { DataGrid, GridColDef, GridValueFormatterParams, GridValueGetterParams, GridRenderCellParams } from '@mui/x-data-grid';
import ChartModal from './ChartModal';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import StopIcon from '@mui/icons-material/Stop';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import { getPositionSideLabel } from '../utils/positions';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const InstanceView: React.FC = () => {
  const { name } = useParams<{ name: string }>();
  const { token, isAdmin } = useAuth();
  const instanceName = name?.toUpperCase(); // Convert to uppercase to match API
  // Default to Monitoring tab (index 0) for instances that have it
  const defaultTab = 0;
  const [activeTab, setActiveTab] = useState(defaultTab);
  // V1 (OPTIONS) has two EOD feeds: the account EOD files and the OPT-SPY-DAILY strategy feed
  // Helper function to get auth headers (inlined where used to ensure fresh token)
  const [realtimeData, setRealtimeData] = useState<any[]>([]);
  const [appLogsData, setAppLogsData] = useState<any>(null);
  const [appLogsLoading, setAppLogsLoading] = useState(false);
  const [appLogsError, setAppLogsError] = useState<string | null>(null);
  const [appLogLines, setAppLogLines] = useState(100);
  const [autoRefreshLogs, setAutoRefreshLogs] = useState(false);
  const [ordersData, setOrdersData] = useState<any[]>([]);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [ordersError, setOrdersError] = useState<string | null>(null);
  const [ordersFilters, setOrdersFilters] = useState<any>({
    start_date: new Date().toISOString().split('T')[0],
    end_date: new Date().toISOString().split('T')[0],
    status: 'all'
  });
  const [isRefreshingFromImport, setIsRefreshingFromImport] = useState(false);
  
  // Positions and account summary data
  const [positionsData, setPositionsData] = useState<any[]>([]);
  const [positionsLoading, setPositionsLoading] = useState(false);
  const [positionsError, setPositionsError] = useState<string | null>(null);
  const [accountSummary, setAccountSummary] = useState<any>(null);
  const [accountSummaryLoading, setAccountSummaryLoading] = useState(false);
  const [accountSummaryError, setAccountSummaryError] = useState<string | null>(null);
  
  // Trade pairs data
  const [tradePairsData, setTradePairsData] = useState<any[]>([]);
  const [tradePairsResponse, setTradePairsResponse] = useState<any>(null);
  const [tradePairsLoading, setTradePairsLoading] = useState(false);
  const [tradePairsError, setTradePairsError] = useState<string | null>(null);
  
  // VWAP data removed - tab no longer in use
  
  // HYBRID Service Control (for HYBRID instance only)
  const [serviceStatus, setServiceStatus] = useState<any>(null);
  const [serviceLoading, setServiceLoading] = useState(false);
  const [serviceMode, setServiceMode] = useState<string>('default');
  const [stopConfirmOpen, setStopConfirmOpen] = useState(false);
  
  // RUN APP functionality
  const [runAppLoading, setRunAppLoading] = useState(false);
  const [runAppResult, setRunAppResult] = useState<{
    status: 'success' | 'error' | null;
    message: string;
    output?: string;
    error?: string;
  } | null>(null);
  
  // STOP APP functionality
  const [stopAppLoading, setStopAppLoading] = useState(false);
  const [stopAppResult, setStopAppResult] = useState<{
    status: 'success' | 'error' | 'warning' | null;
    message: string;
    output?: string;
    error?: string;
    remaining_processes?: string[];
  } | null>(null);
  
  // App status tracking
  const [appStatus, setAppStatus] = useState<{
    is_running: boolean;
    process_count: number;
    running_processes: string[];
  } | null>(null);
  const [appStatusLoading, setAppStatusLoading] = useState(false);

  // Chart modal state
  const [chartModalOpen, setChartModalOpen] = useState(false);
  const [selectedSymbol, setSelectedSymbol] = useState<string>('');

  // Log streaming state (HYBRID only)
  const [logLines, setLogLines] = useState<string[]>([]);
  const [isLogPaused, setIsLogPaused] = useState(false);
  const [autoScroll, setAutoScroll] = useState(true);
  const [logError, setLogError] = useState<string | null>(null);
  const logContainerRef = React.useRef<HTMLDivElement>(null);

  // Chart state (HYBRID only)
  const [chartDate, setChartDate] = useState<string>('');
  const [chartSymbol, setChartSymbol] = useState<string>('');
  const [chartTimeframe, setChartTimeframe] = useState<string>('15');
  const [chartData, setChartData] = useState<any>(null);
  const [chartLoading, setChartLoading] = useState(false);
  const [chartError, setChartError] = useState<string | null>(null);
  const [availableSymbols, setAvailableSymbols] = useState<string[]>([]);
  const [latestEodDate, setLatestEodDate] = useState<string>('');
  const chartContainerRef = React.useRef<HTMLDivElement>(null);

  // Timing parameters for 6-main-time-v2.py
  const [timingParams, setTimingParams] = useState<{
    t2: number;
    t3: number;
    t4?: number; // Only for V1
  }>({
    t2: 5,   // Default 5 minutes
    t3: 10,  // Default 10 minutes
    t4: 15   // Default 15 minutes (V1 only)
  });

  console.log('InstanceView mounted with name:', name, '->', instanceName);

  // Generate minute options: 1,2,3,4,5,10,15,20,30...220
  const generateMinuteOptions = () => {
    const options = [];
    // 1-5 minutes
    for (let i = 1; i <= 5; i++) {
      options.push(i);
    }
    // 10,15,20,30,40,50,60,70,80,90,100...220
    for (let i = 10; i <= 220; i += 10) {
      options.push(i);
    }
    // Add 15,20 separately if not already included
    if (!options.includes(15)) options.push(15);
    if (!options.includes(20)) options.push(20);
    return options.sort((a, b) => a - b);
  };

  const minuteOptions = generateMinuteOptions();

  // Determine which timing parameters to show based on instance
  const isV1Instance = instanceName === 'V1';
  const showT4 = isV1Instance; // Only V1 has t4

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  // Chart modal handlers
  const handleSymbolClick = (symbol: string) => {
    setSelectedSymbol(symbol);
    setChartModalOpen(true);
  };

  const handleCloseChartModal = () => {
    setChartModalOpen(false);
    setSelectedSymbol('');
  };

  // Check app status
  const checkAppStatus = useCallback(async () => {
    if (!instanceName) return;
    
    setAppStatusLoading(true);
    try {
      const response = await fetch(`/api/instances/${instanceName}/app-status`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const result = await response.json();
      setAppStatus({
        is_running: result.is_running,
        process_count: result.process_count,
        running_processes: result.running_processes,
      });
    } catch (error) {
      console.error('Error checking app status:', error);
    } finally {
      setAppStatusLoading(false);
    }
  }, [instanceName, ordersFilters.start_date]);

  // RUN APP function
  const handleRunApp = async () => {
    if (!instanceName) return;
    
    setRunAppLoading(true);
    setRunAppResult(null);
    
    try {
      const response = await fetch(`/api/instances/${instanceName}/run-app`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          timing: timingParams
        }),
      });
      
      const result = await response.json();
      
      setRunAppResult({
        status: result.status,
        message: result.message,
        output: result.output,
        error: result.error,
      });
      
      // If app was started successfully, check status and refresh data
      if (result.status === 'success') {
        setTimeout(() => {
          checkAppStatus();
          fetchData();
        }, 3000); // Wait 3 seconds then refresh data
      }
    } catch (error) {
      setRunAppResult({
        status: 'error',
        message: 'Failed to start app',
        error: error instanceof Error ? error.message : 'Unknown error',
      });
    } finally {
      setRunAppLoading(false);
    }
  };

  // STOP APP function
  const handleStopApp = async () => {
    if (!instanceName) return;
    
    setStopAppLoading(true);
    setStopAppResult(null);
    
    try {
      const response = await fetch(`/api/instances/${instanceName}/stop-app`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      
      const result = await response.json();
      
      setStopAppResult({
        status: result.status,
        message: result.message,
        output: result.output,
        error: result.error,
        remaining_processes: result.remaining_processes,
      });
      
      // Check status after stopping
      setTimeout(() => {
        checkAppStatus();
      }, 2000);
    } catch (error) {
      setStopAppResult({
        status: 'error',
        message: 'Failed to stop app',
        error: error instanceof Error ? error.message : 'Unknown error',
      });
    } finally {
      setStopAppLoading(false);
    }
  };

  // Memoize fetchData to prevent infinite loop
  const fetchData = useCallback(async () => {
    if (!token || !instanceName) return; // Don't fetch without token or instanceName
    
    // Skip for ORB-style instances - they use ORBMonitoring component with its own data fetching
    const orbStyleInstances = ['V1', 'E_OPTIONS', 'OPTIONS_PCS', 'HYBRID', 'TOPORB', 'TIER3', 'LIVE', 'ORB_AVI', 'ORB_JOHN', 'GROK', 'LADYJANE', 'FUTURES'];
    if (orbStyleInstances.includes(instanceName)) {
      return; // Skip - these instances don't use realtime/top5/top50 endpoints
    }
    
    console.log(`Fetching data for: ${instanceName}`);
    try {
      const headers = { 'Authorization': `Bearer ${token}` };
      const [realtimeRes, top5Res, top50Res] = await Promise.all([
        fetch(`/api/instances/${instanceName}/realtime`, { headers }),
        fetch(`/api/instances/${instanceName}/top5`, { headers }),
        fetch(`/api/instances/${instanceName}/top50`, { headers }),
      ]);

      if (!top5Res.ok) {
        const text = await top5Res.text();
        console.error('Error response from top5 endpoint:', text);
        throw new Error(`HTTP error! status: ${top5Res.status}`);
      }

      const realtime = await realtimeRes.json();
      const top5 = await top5Res.json();
      const top50 = await top50Res.json();

      console.log(`${instanceName} - Top5 data:`, top5.candidates?.length || 0);
      console.log(`${instanceName} - Top5 status:`, top5.status);
      console.log(`${instanceName} - Top5 message:`, top5.message);

      setRealtimeData(realtime.orders?.map((item: any, index: number) => ({ 
        ...item, 
        id: index,
        submitted_at: item.submitted_at ? new Date(item.submitted_at).toLocaleString() : '',
        filled_at: item.filled_at ? new Date(item.filled_at).toLocaleString() : '',
        instance: instanceName
      })) || []);

      // Top5 and Top50 data handling removed - tabs no longer in use
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  }, [instanceName, ordersFilters.start_date, token]);

  // fetchVwapData removed - VWAP tab no longer in use

  // Check app status on component mount
  useEffect(() => {
    if (instanceName) {
      checkAppStatus();
    }
  }, [instanceName, checkAppStatus]);

  // Auto-refresh for VWAP data when enabled and tab is active
  // VWAP auto-refresh removed - tab no longer in use

  // Log streaming for all instances.
  // 10/22 fix: the Logs tab is index 6 on V1 (Live Trade + Gamma Map +
  // Shadows shift it) and 4 everywhere else — this was hardcoded to 4, so on
  // V1/OPTIONS the stream connected on the Shadows tab (invisibly) and never
  // on the actual Logs tab, which therefore sat frozen. Must match the
  // TabPanel index below.
  const logsTabIndex = instanceName === 'V1' ? 6 : 4;
  
  useEffect(() => {
    if (activeTab !== logsTabIndex) {
      return; // Only connect when Logs tab is active
    }

    let eventSource: EventSource | null = null;
    const MAX_LINES = 500; // Keep last 500 lines for performance

    const connectLogStream = () => {
      try {
        setLogError(null);
        // EventSource doesn't support custom headers, so pass token as query param
        const url = `/api/instances/${instanceName}/logs/stream?token=${encodeURIComponent(token || '')}`;
        eventSource = new EventSource(url);

        eventSource.onopen = () => {
          console.log(`Log stream connected for ${instanceName}`);
          setLogError(null);
        };

        eventSource.onmessage = (event) => {
          if (isLogPaused) return; // Don't update if paused

          try {
            const data = JSON.parse(event.data);
            if (data.error) {
              setLogError(data.error);
              return;
            }
            if (data.line) {
              setLogLines((prev) => {
                const newLines = [...prev, data.line];
                // Keep only last MAX_LINES
                return newLines.slice(-MAX_LINES);
              });
            }
          } catch (err) {
            console.error('Error parsing log event:', err);
          }
        };

        eventSource.onerror = (error) => {
          console.error(`Log stream error for ${instanceName}:`, error);
          setLogError('Connection lost. Reconnecting...');
          eventSource?.close();
          
          // Attempt to reconnect after 3 seconds
          setTimeout(() => {
            if (activeTab === logsTabIndex) {
              connectLogStream();
            }
          }, 3000);
        };
      } catch (err) {
        console.error('Error setting up log stream:', err);
        setLogError(err instanceof Error ? err.message : 'Failed to connect');
      }
    };

    connectLogStream();

    // Cleanup function
    return () => {
      if (eventSource) {
        console.log(`Closing log stream connection for ${instanceName}`);
        eventSource.close();
      }
      // Clear logs when leaving the tab
      setLogLines([]);
      setLogError(null);
    };
  }, [instanceName, activeTab, isLogPaused, token, logsTabIndex]);

  // Auto-scroll effect for logs
  useEffect(() => {
    if (autoScroll && logContainerRef.current) {
      logContainerRef.current.scrollTop = logContainerRef.current.scrollHeight;
    }
  }, [logLines, autoScroll]);

  // Fetch latest EOD date on mount for Charts tab
  useEffect(() => {
    if (activeTab === 3) {
      fetchLatestEodDate();
    }
  }, [instanceName, activeTab]);

  // Chart rendering effect for all instances
  useEffect(() => {
    if (!chartData || !chartContainerRef.current) {
      return;
    }
    
    // Clear previous chart
    chartContainerRef.current.innerHTML = '';

    // Create chart with dark theme
    const chart = createChart(chartContainerRef.current, {
      layout: {
        background: { type: ColorType.Solid, color: '#1e1e1e' },
        textColor: '#d1d4dc',
      },
      width: chartContainerRef.current.clientWidth,
      height: 600,
      timeScale: {
        timeVisible: true,
        secondsVisible: false,
      },
      grid: {
        vertLines: { color: 'rgba(42, 46, 57, 0.6)' },
        horzLines: { color: 'rgba(42, 46, 57, 0.6)' },
      },
    });

    // Add candlestick series
    const candlestickSeries = chart.addSeries(CandlestickSeries, {
      upColor: '#26a69a',
      downColor: '#ef5350',
      wickUpColor: '#26a69a',
      wickDownColor: '#ef5350',
    });

    // Set candlestick data
    candlestickSeries.setData(chartData.bars);

    // Add trade markers
    if (chartData.trade_pairs && chartData.trade_pairs.length > 0) {
      const markers: any[] = [];
      
      chartData.trade_pairs.forEach((trade: any, idx: number) => {
        // Parse ISO timestamps (they include timezone offset like -04:00 for EDT)
        const entryDate = new Date(trade.entry_time_et);
        const exitDate = new Date(trade.exit_time_et);
        
        // Convert to Unix timestamp in seconds (UTC internally)
        // Then subtract 4 hours to align with ET (matching Polygon data conversion in backend)
        const ET_OFFSET_SECONDS = 4 * 3600; // 4 hours for EDT
        const entryTimestamp = Math.floor(entryDate.getTime() / 1000) - ET_OFFSET_SECONDS;
        const exitTimestamp = Math.floor(exitDate.getTime() / 1000) - ET_OFFSET_SECONDS;
        
        // Get P&L from realized_pnl field
        const pnl = parseFloat(trade.realized_pnl || trade.pnl_dollars || 0);
        const isWin = pnl > 0;
        const entryPrice = parseFloat(trade.entry_price);
        const exitPrice = parseFloat(trade.exit_price);
        const side = (trade.side || 'LONG').toUpperCase();
        const instrumentType = trade.instrument_type || 'EQUITY';
        const instrumentBadge = instrumentType === 'OPTION' ? 'OPT' : 'STK';
        
        // For SHORT trades, reverse the markers (entry = down, exit = up)
        const isShort = side === 'SHORT';
        const quantity = trade.qty || trade.quantity || 0;
        
        // Add ENTRY marker with quantity
        markers.push({
          time: entryTimestamp as any,
          position: isShort ? 'aboveBar' as any : 'belowBar' as any,
          color: '#2196F3',
          shape: isShort ? 'arrowDown' as any : 'arrowUp' as any,
          text: `${side} ${instrumentBadge} x${quantity} @$${entryPrice.toFixed(2)}`
        });
        
        // Add EXIT marker with quantity and P&L
        markers.push({
          time: exitTimestamp as any,
          position: isShort ? 'belowBar' as any : 'aboveBar' as any,
          color: isWin ? '#4CAF50' : '#F44336',
          shape: isShort ? 'arrowUp' as any : 'arrowDown' as any,
          text: `x${quantity} @$${exitPrice.toFixed(2)} | P&L: ${isWin ? '+' : ''}$${pnl.toFixed(2)}`
        });
      });
      
      createSeriesMarkers(candlestickSeries, markers);
    }

    // Fit content
    chart.timeScale().fitContent();

    // Handle resize
    const handleResize = () => {
      if (chartContainerRef.current) {
        chart.applyOptions({ width: chartContainerRef.current.clientWidth });
      }
    };
    window.addEventListener('resize', handleResize);

    // Cleanup
    return () => {
      window.removeEventListener('resize', handleResize);
      chart.remove();
    };
  }, [chartData, instanceName]);


  // Fetch orders data from Alpaca API
  const fetchOrdersData = useCallback(async (filters: any = {}) => {
    if (!instanceName) return;
    
    setOrdersLoading(true);
    setOrdersError(null);
    
    try {
      const params = new URLSearchParams();
      if (filters.start_date) params.append('start_date', filters.start_date);
      if (filters.end_date) params.append('end_date', filters.end_date);
      // Note: We don't send status to API since Alpaca only supports 'open', 'closed', 'all'
      // We'll filter by status on the frontend

      const response = await fetch(`/api/instances/${instanceName}/orders?${params.toString()}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP error! status: ${response.status} - ${errorText}`);
      }
      
      const data = await response.json();
      
      // Sort by submitted_at (desc)
      const sortedOrders = (data.orders || []).slice().sort((a: any, b: any) => {
        const aDate = a.submitted_at ? new Date(a.submitted_at) : new Date(0);
        const bDate = b.submitted_at ? new Date(b.submitted_at) : new Date(0);
        return bDate.getTime() - aDate.getTime();
      });
      
      // Apply status filter on frontend
      let filteredOrders = sortedOrders;
      if (filters.status && filters.status !== 'all') {
        filteredOrders = sortedOrders.filter((order: any) => order.status === filters.status);
      }
      
      setOrdersData(filteredOrders.map((item: any, index: number) => ({ 
        ...item, 
        id: index,
        submitted_at: item.submitted_at ? new Date(item.submitted_at).toLocaleString() : '',
        filled_at: item.filled_at ? new Date(item.filled_at).toLocaleString() : '',
        canceled_at: item.canceled_at ? new Date(item.canceled_at).toLocaleString() : '',
        expired_at: item.expired_at ? new Date(item.expired_at).toLocaleString() : '',
        replaced_at: item.replaced_at ? new Date(item.replaced_at).toLocaleString() : '',
        instance: instanceName
      })) || []);
    } catch (error) {
      console.error('Error fetching orders data:', error);
      setOrdersError(error instanceof Error ? error.message : 'Unknown error occurred');
    } finally {
      setOrdersLoading(false);
    }
  }, [instanceName]);

  // Fetch positions data from Alpaca API
  const fetchPositionsData = useCallback(async () => {
    if (!instanceName) return;
    
    setPositionsLoading(true);
    setPositionsError(null);
    
    try {
      const response = await fetch(`/api/instances/${instanceName}/positions`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP error! status: ${response.status} - ${errorText}`);
      }
      
      const data = await response.json();
      setPositionsData(data.positions || []);
    } catch (error) {
      console.error('Error fetching positions data:', error);
      setPositionsError(error instanceof Error ? error.message : 'Unknown error occurred');
    } finally {
      setPositionsLoading(false);
    }
  }, [instanceName]);

  // Fetch account summary data from Alpaca API
  const fetchAccountSummary = useCallback(async (dateOverride?: string) => {
    if (!instanceName) return;
    
    setAccountSummaryLoading(true);
    setAccountSummaryError(null);
    
    try {
      // If a historical date is selected in Orders (or explicitly provided), pass it so backend can compute that day's Daily P&L
      const selectedDate = dateOverride || ordersFilters?.start_date;
      const dateParam = selectedDate ? `?date=${encodeURIComponent(selectedDate)}` : '';
      const response = await fetch(`/api/instances/${instanceName}/account-summary${dateParam}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP error! status: ${response.status} - ${errorText}`);
      }
      
      const data = await response.json();
      setAccountSummary(data.account_summary || null);
    } catch (error) {
      console.error('Error fetching account summary data:', error);
      setAccountSummaryError(error instanceof Error ? error.message : 'Unknown error occurred');
    } finally {
      setAccountSummaryLoading(false);
    }
  }, [instanceName, ordersFilters.start_date]);

  // Fetch trade pairs data from Alpaca API
  const fetchTradePairsData = useCallback(async () => {
    if (!instanceName) return;
    
    setTradePairsLoading(true);
    setTradePairsError(null);
    
    try {
      const startDate = ordersFilters.start_date;
      const endDate = ordersFilters.end_date;
      const response = await fetch(`/api/instances/${instanceName}/trade-pairs?start_date=${startDate}&end_date=${endDate}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP error! status: ${response.status} - ${errorText}`);
      }
      
      const data = await response.json();
      setTradePairsData(data.trade_pairs || []);
      setTradePairsResponse(data); // Store full response for per_instrument_type
    } catch (error) {
      console.error('Error fetching trade pairs data:', error);
      setTradePairsError(error instanceof Error ? error.message : 'Unknown error occurred');
    } finally {
      setTradePairsLoading(false);
    }
  }, [instanceName, ordersFilters.start_date, ordersFilters.end_date]);


  // Fetch app logs data
  const fetchAppLogsData = useCallback(async () => {
    if (!instanceName) return;
    
    setAppLogsLoading(true);
    setAppLogsError(null);
    
    try {
      const response = await fetch(`/api/instances/${instanceName}/app-logs?lines=${appLogLines}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP error! status: ${response.status} - ${errorText}`);
      }
      
      const data = await response.json();
      setAppLogsData(data);
    } catch (error) {
      console.error('Error fetching app logs data:', error);
      setAppLogsError(error instanceof Error ? error.message : 'Unknown error occurred');
    } finally {
      setAppLogsLoading(false);
    }
  }, [instanceName, appLogLines]);

  // Legacy HYBRID Service Control Functions
  const fetchServiceStatus = useCallback(async () => {
    if (instanceName !== 'HYBRID') return;
    
    try {
      const response = await fetch(`/api/instances/${instanceName}/service/status`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) throw new Error('Failed to fetch service status');
      
      const data = await response.json();
      setServiceStatus(data);
      
      // Update mode selector to match current running mode
      if (data.current_mode !== 'inactive') {
        setServiceMode(data.current_mode);
      }
    } catch (error) {
      console.error('Error fetching service status:', error);
    }
  }, [instanceName]);

  const handleStartService = async () => {
    setServiceLoading(true);
    try {
      const response = await fetch(`/api/instances/${instanceName}/service/start?mode=${serviceMode}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) {
        const error = await response.json();
        if (response.status === 401) {
          throw new Error('Session expired. Please refresh the page and log in again.');
        }
        throw new Error(error.detail || 'Failed to start service');
      }
      
      const result = await response.json();
      
      const modeLabel = serviceMode === 'default' ? 'Hybrid' : serviceMode === 'stocks' ? 'Stocks' : 'Options';
      
      // Show success message
      setRunAppResult({
        status: 'success',
        message: `✅ Service started in ${modeLabel} mode`,
        output: result.message
      });
      
      // Refresh status after a short delay
      setTimeout(() => {
        fetchServiceStatus();
      }, 2000);
      
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      const isTokenExpired = errorMessage.includes('Session expired') || errorMessage.includes('Token has expired');
      
      setRunAppResult({
        status: 'error',
        message: isTokenExpired ? '🔒 Session Expired' : '❌ Failed to start service',
        output: errorMessage + (isTokenExpired ? '\n\nPlease refresh the page (F5) to continue.' : '')
      });
    } finally {
      setServiceLoading(false);
    }
  };

  const handleStopService = async () => {
    setStopConfirmOpen(false);
    setServiceLoading(true);
    setRunAppResult(null);
    
    try {
      const response = await fetch(`/api/instances/${instanceName}/service/stop`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) {
        const error = await response.json();
        if (response.status === 401) {
          throw new Error('Session expired. Please refresh the page and log in again.');
        }
        throw new Error(error.detail || 'Failed to stop service');
      }
      
      const result = await response.json();
      
      // Show success message
      setRunAppResult({
        status: 'success',
        message: '✅ Service stopped',
        output: result.message
      });
      
      // Refresh status after a short delay
      setTimeout(() => {
        fetchServiceStatus();
      }, 2000);
      
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      const isTokenExpired = errorMessage.includes('Session expired') || errorMessage.includes('Token has expired');
      
      setRunAppResult({
        status: 'error',
        message: isTokenExpired ? '🔒 Session Expired' : '❌ Failed to stop service',
        output: errorMessage + (isTokenExpired ? '\n\nPlease refresh the page (F5) to continue.' : '')
      });
    } finally {
      setServiceLoading(false);
    }
  };

  const handleRestartService = async () => {
    setServiceLoading(true);
    setRunAppResult(null);
    
    try {
      const response = await fetch(`/api/instances/${instanceName}/service/restart`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) {
        const error = await response.json();
        if (response.status === 401) {
          throw new Error('Session expired. Please refresh the page and log in again.');
        }
        throw new Error(error.detail || 'Failed to restart service');
      }
      
      const result = await response.json();
      
      // Show success message
      setRunAppResult({
        status: 'success',
        message: '✅ Service restarted',
        output: result.message
      });
      
      // Refresh status after a short delay
      setTimeout(() => {
        fetchServiceStatus();
      }, 2000);
      
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      const isTokenExpired = errorMessage.includes('Session expired') || errorMessage.includes('Token has expired');
      
      setRunAppResult({
        status: 'error',
        message: isTokenExpired ? '🔒 Session Expired' : '❌ Failed to restart service',
        output: errorMessage + (isTokenExpired ? '\n\nPlease refresh the page (F5) to continue.' : '')
      });
    } finally {
      setServiceLoading(false);
    }
  };

  // Handle mode change with auto-restart
  const handleModeChange = async (event: React.MouseEvent<HTMLElement>, newMode: string | null) => {
    if (!newMode || newMode === serviceMode || !serviceStatus?.is_running) return;
    
    setServiceMode(newMode);
    setServiceLoading(true);
    setRunAppResult(null);
    
    try {
      // Stop all services first
      const stopResponse = await fetch(`/api/instances/${instanceName}/service/stop`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!stopResponse.ok) {
        const error = await stopResponse.json();
        // Handle token expiration
        if (stopResponse.status === 401) {
          throw new Error('Session expired. Please refresh the page and log in again.');
        }
        throw new Error(error.detail || 'Failed to stop service');
      }
      
      // Wait a moment for services to stop
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      // Start with new mode
      const response = await fetch(`/api/instances/${instanceName}/service/start?mode=${newMode}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) {
        const error = await response.json();
        // Handle token expiration
        if (response.status === 401) {
          throw new Error('Session expired. Please refresh the page and log in again.');
        }
        throw new Error(error.detail || 'Failed to switch mode');
      }
      
      const result = await response.json();
      
      const modeLabel = newMode === 'default' ? 'Hybrid' : newMode === 'stocks' ? 'Stocks' : 'Options';
      
      // Show success message
      setRunAppResult({
        status: 'success',
        message: `✅ Switched to ${modeLabel} mode`,
        output: result.message
      });
      
      // Refresh status after a short delay
      setTimeout(() => {
        fetchServiceStatus();
      }, 2000);
      
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      const isTokenExpired = errorMessage.includes('Session expired') || errorMessage.includes('Token has expired');
      
      setRunAppResult({
        status: 'error',
        message: isTokenExpired ? '🔒 Session Expired' : '❌ Failed to switch mode',
        output: errorMessage + (isTokenExpired ? '\n\nPlease refresh the page (F5) to continue.' : '')
      });
      // Revert mode on error
      setServiceMode(serviceStatus?.current_mode || 'default');
    } finally {
      setServiceLoading(false);
    }
  };

  // Chart functions for HYBRID
  const fetchLatestEodDate = async () => {
    try {
      const response = await fetch('/api/instances/HYBRID/eod-dates', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) return;
      
      const data = await response.json();
      if (data.dates && data.dates.length > 0) {
        // Dates are returned sorted ascending, so last element is most recent
        const sortedDates = [...data.dates].sort();
        const latestDate = sortedDates[sortedDates.length - 1];
        setLatestEodDate(latestDate);
        setChartDate(latestDate);
        // Fetch symbols for this date
        fetchAvailableSymbols(latestDate);
      }
    } catch (err) {
      console.error('Error fetching latest EOD date:', err);
    }
  };

  const fetchAvailableSymbols = async (date: string) => {
    try {
      const response = await fetch(`/api/instances/${instanceName}/eod-data?date=${date}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch EOD data');
      }
      
      const responseData = await response.json();
      const eodData = responseData.data || responseData;
      
      if (eodData.pairs && Array.isArray(eodData.pairs)) {
        const symbolSet = new Set(eodData.pairs.map((p: any) => p.symbol));
        const symbols = Array.from(symbolSet).sort() as string[];
        setAvailableSymbols(symbols);
        
        // Auto-select first symbol
        if (symbols.length > 0 && !chartSymbol) {
          setChartSymbol(symbols[0]);
        }
      } else {
        setAvailableSymbols([]);
      }
    } catch (err: any) {
      console.error('Error fetching available symbols:', err);
      setAvailableSymbols([]);
    }
  };

  const loadChartData = async () => {
    if (!chartDate || !chartSymbol) return;
    
    setChartLoading(true);
    setChartError(null);
    
    try {
      const response = await fetch(
        `/api/instances/${instanceName}/chart-data?symbol=${chartSymbol}&date=${chartDate}&timeframe=${chartTimeframe}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to load chart data');
      }
      
      const data = await response.json();
      
      if (data.bars.length === 0) {
        setChartError(`No market data available for ${chartSymbol} on ${chartDate}. Market may have been closed or symbol not traded.`);
        setChartData(null);
        return;
      }
      
      setChartData(data);
    } catch (err: any) {
      setChartError(err.message);
      setChartData(null);
    } finally {
      setChartLoading(false);
    }
  };

  // Fetch service status on mount and periodically for legacy HYBRID
  useEffect(() => {
    if (instanceName === 'HYBRID') {
      fetchServiceStatus();
      const interval = setInterval(fetchServiceStatus, 30000); // Every 30 seconds
      return () => clearInterval(interval);
    }
  }, [instanceName, fetchServiceStatus]);

  // Handle orders filter changes
  const handleOrdersFiltersChange = (filters: any) => {
    setOrdersFilters(filters);
    fetchOrdersData(filters);
  };

  // Use polling with a reasonable interval (e.g., 30 seconds)
  // Skip for ORB-style instances - they use ORBMonitoring component with its own data fetching
  useEffect(() => {
    if (!instanceName) return; // Guard against undefined
    
    const orbStyleInstances = ['V1', 'E_OPTIONS', 'OPTIONS_PCS', 'HYBRID', 'TOPORB', 'TIER3', 'LIVE', 'ORB_AVI', 'ORB_JOHN', 'GROK', 'FUTURES'];
    if (orbStyleInstances.includes(instanceName)) {
      return; // Skip fetchData for ORB-style instances
    }
    
    fetchData();
    const interval = setInterval(fetchData, 30000); // 30 seconds
    return () => clearInterval(interval);
  }, [fetchData, instanceName]);

  // Fetch orders data when Orders tab is active
  useEffect(() => {
    const hasMonitorTab = instanceName === 'V1' || instanceName === 'E_OPTIONS' || instanceName === 'OPTIONS_PCS' || instanceName === 'HYBRID' || instanceName === 'TOPORB' || instanceName === 'TIER3' || instanceName === 'LIVE' || instanceName === 'LADYJANE' || instanceName === 'GROK' || instanceName === 'FUTURES';
    const ordersIdx = hasMonitorTab ? 1 : 0;
    if (activeTab === ordersIdx) {
      fetchOrdersData(ordersFilters);
      fetchPositionsData();
      fetchAccountSummary();
      fetchTradePairsData();
    }
  }, [activeTab, instanceName, fetchOrdersData, fetchPositionsData, fetchAccountSummary, fetchTradePairsData, ordersFilters]);

  // Fetch app logs data when App Logs tab is active
  useEffect(() => {
    if (activeTab === 5) {
      fetchAppLogsData();
    }
  }, [activeTab, fetchAppLogsData]);


  // Listen for data import success events to trigger immediate refresh
  useEffect(() => {
    const handleDataImportSuccess = () => {
      console.log('Data import success detected, triggering immediate refresh for', instanceName);
      // Show refreshing indicator
      setIsRefreshingFromImport(true);
      
      // Add a small delay to ensure backend has processed the imported data
      setTimeout(() => {
        // Trigger immediate refresh of all data
        fetchData();
        const hasMonitorTab = instanceName === 'V1' || instanceName === 'E_OPTIONS' || instanceName === 'OPTIONS_PCS' || instanceName === 'HYBRID' || instanceName === 'TOPORB' || instanceName === 'TIER3' || instanceName === 'LIVE' || instanceName === 'LADYJANE' || instanceName === 'GROK' || instanceName === 'FUTURES';
        const ordersIdx = hasMonitorTab ? 1 : 0;
        if (activeTab === ordersIdx) {
          fetchOrdersData(ordersFilters);
          fetchPositionsData();
          fetchAccountSummary();
          fetchTradePairsData();
        }
        
        // Hide refreshing indicator after a short delay
        setTimeout(() => {
          setIsRefreshingFromImport(false);
        }, 2000);
      }, 1000); // 1 second delay to ensure backend processing
    };

    window.addEventListener('dataImportSuccess', handleDataImportSuccess);
    
    return () => {
      window.removeEventListener('dataImportSuccess', handleDataImportSuccess);
    };
  }, [fetchData, fetchOrdersData, fetchPositionsData, fetchAccountSummary, fetchTradePairsData, ordersFilters, activeTab, instanceName]);

  // Clear Orders-related state on instance change to prevent stale data
  useEffect(() => {
    setOrdersData([]);
    setTradePairsData([]);
    setPositionsData([]);
    setAccountSummary(null);
  }, [instanceName]);

  // Auto-refresh for app logs when enabled and tab is active
  useEffect(() => {
    if (autoRefreshLogs && activeTab === 5) {
      const interval = setInterval(() => {
        fetchAppLogsData();
      }, 3000); // Refresh every 3 seconds
      
      return () => clearInterval(interval);
    }
  }, [autoRefreshLogs, activeTab, fetchAppLogsData]);

  // Periodic app status check - every 10 seconds
  useEffect(() => {
    if (instanceName) {
      // Initial check
      checkAppStatus();
      
      // Set up periodic checking
      const interval = setInterval(() => {
        checkAppStatus();
      }, 10000); // Check every 10 seconds
      
      return () => clearInterval(interval);
    }
  }, [instanceName, checkAppStatus]);

  // Auto-refresh removed - use manual refresh button instead

  // Get Top 5 specific columns with all requested fields
  const getTop5Columns = (): GridColDef[] => {
    const base: GridColDef[] = [
      {
        field: 'symbol',
        headerName: 'Symbol',
        width: 100,
        renderCell: (params: GridRenderCellParams) => {
          const symbol = params.value;
          if (!symbol) return params.value;
          
          return (
            <Typography
              variant="body2"
              onClick={() => handleSymbolClick(symbol)}
              sx={{ 
                color: '#90caf9', 
                textDecoration: 'none',
                fontWeight: 'bold',
                cursor: 'pointer',
                '&:hover': {
                  textDecoration: 'underline'
                }
              }}
            >
              {symbol}
            </Typography>
          );
        }
      },
      {
        field: 'ai_sentiment',
        headerName: 'AI Sentiment',
        width: 120,
        renderCell: (params: GridRenderCellParams) => {
          const sentiment = (params.value || '').toString().toLowerCase();
          let color: any = 'default';
          if (sentiment === 'bullish') color = 'success';
          else if (sentiment === 'bearish') color = 'error';
          return <Chip label={params.value || 'N/A'} color={color} size="small" />;
        }
      },
      {
        field: 'ai_risk',
        headerName: 'AI Risk',
        width: 100,
        renderCell: (params: GridRenderCellParams) => {
          const risk = (params.value || '').toString().toLowerCase();
          let color: any = 'default';
          if (risk === 'low') color = 'success';
          else if (risk === 'medium') color = 'warning';
          else if (risk === 'high') color = 'error';
          return <Chip label={params.value || 'N/A'} color={color} size="small" />;
        }
      },
      {
        field: 'ai_filter',
        headerName: 'AI Filter',
        width: 100,
        valueGetter: (params: GridValueGetterParams) => {
          const status = params.row.status;
          if (status === 'passed') return 'Passed';
          if (status === 'failed') return 'Failed';
          if (status === 'no_ai') return 'No AI';
          return 'Unknown';
        },
        renderCell: (params: GridRenderCellParams) => {
          const value = params.value;
          let color: any = 'default';
          if (value === 'Passed') color = 'success';
          else if (value === 'Failed') color = 'error';
          else if (value === 'No AI') color = 'warning';
          return <Chip label={value} color={color} size="small" />;
        }
      },
      {
        field: 'ai_reasoning',
        headerName: 'Reasoning',
        width: 340,
        renderCell: (params: GridRenderCellParams) => {
          const reasoning = params.value;
          if (!reasoning) return <Typography variant="body2" color="text.secondary">No reasoning available</Typography>;
          return (
            <Typography 
              variant="body2" 
              sx={{ whiteSpace: 'normal', wordWrap: 'break-word', lineHeight: 1.2, fontSize: '0.875rem' }}
              title={reasoning}
            >
              {reasoning}
            </Typography>
          );
        }
      },
    ];

    // Append Ladyjane pre/post columns for visibility
    if (instanceName === 'LADYJANE') {
      base.push(
        {
          field: 'pre_ai_sentiment',
          headerName: 'Pre Sentiment',
          width: 130,
          renderCell: (p: GridRenderCellParams) => <Chip label={p.value || '—'} size="small" />,
        },
        {
          field: 'pre_ai_risk',
          headerName: 'Pre Risk',
          width: 110,
          renderCell: (p: GridRenderCellParams) => <Chip label={p.value || '—'} size="small" />,
        },
        {
          field: 'pre_ai_reasoning',
          headerName: 'Pre Reasoning',
          width: 260,
          renderCell: (p: GridRenderCellParams) => (
            <Typography variant="body2" sx={{ whiteSpace: 'normal' }} title={p.value || ''}>
              {p.value || '—'}
            </Typography>
          ),
        },
        {
          field: 'post_ai_sentiment',
          headerName: 'Post Sentiment',
          width: 140,
          renderCell: (p: GridRenderCellParams) => (
            <Chip label={p.value || '—'} size="small" color={((p.value || '').toString().toLowerCase()==='bullish') ? 'success' : ((p.value || '').toString().toLowerCase()==='bearish') ? 'error' : 'default'} />
          ),
        },
        {
          field: 'post_ai_risk',
          headerName: 'Post Risk',
          width: 120,
          renderCell: (p: GridRenderCellParams) => (
            <Chip label={p.value || '—'} size="small" color={((p.value || '').toString().toLowerCase()==='low') ? 'success' : ((p.value || '').toString().toLowerCase()==='medium') ? 'warning' : ((p.value || '').toString().toLowerCase()==='high') ? 'error' : 'default'} />
          ),
        },
        {
          field: 'post_ai_reasoning',
          headerName: 'Post Reasoning',
          width: 320,
          renderCell: (p: GridRenderCellParams) => (
            <Typography variant="body2" sx={{ whiteSpace: 'normal' }} title={p.value || ''}>
              {p.value || '—'}
            </Typography>
          ),
        },
      );
    }

    // Instance chip and date/price columns
    base.push(
      {
        field: 'instance_name',
        headerName: 'Instance',
        width: 100,
        valueGetter: () => instanceName,
        renderCell: () => (
          <Typography variant="body2" fontWeight="bold">{instanceName}</Typography>
        )
      },
      {
        field: 'date',
        headerName: 'Date',
        width: 120,
        renderCell: (params: GridRenderCellParams) => (
          <Typography variant="body2">{params.value || ''}</Typography>
        )
      },
      {
        field: 'close_price',
        headerName: 'Close Price',
        width: 120,
        renderCell: (params: GridRenderCellParams) => (
          <Typography variant="body2">{params.value != null ? `$${parseFloat(params.value).toFixed(2)}` : 'N/A'}</Typography>
        )
      },
      {
        field: 'position_type',
        headerName: 'Position Type',
        width: 120,
        renderCell: (params: GridRenderCellParams) => {
          const type = params.value;
          const color: any = type === 'LONG' ? 'success' : type === 'SHORT' ? 'error' : 'default';
          return <Chip label={type || 'N/A'} color={color} size="small" />;
        }
      },
      {
        field: 'created_at',
        headerName: 'Created At',
        width: 180,
        renderCell: (params: GridRenderCellParams) => (
          <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{params.value || ''}</Typography>
        )
      },
    );

    return base;
  };

  // Get AI filter columns for historical view
  const getAIFilterColumns = (): GridColDef[] => [
    {
      field: 'ai_sentiment',
      headerName: 'AI Sentiment',
      width: 120,
      type: 'string',
      renderCell: (params: GridRenderCellParams) => {
        const sentiment = params.value?.toLowerCase();
        let color = 'default';
        if (sentiment === 'bullish') color = 'success';
        else if (sentiment === 'bearish') color = 'error';
        return <Chip label={params.value} color={color as any} size="small" />;
      }
    },
    {
      field: 'ai_risk',
      headerName: 'AI Risk',
      width: 100,
      type: 'string',
      renderCell: (params: GridRenderCellParams) => {
        const risk = params.value?.toLowerCase();
        let color = 'default';
        if (risk === 'low') color = 'success';
        else if (risk === 'medium') color = 'warning';
        else if (risk === 'high') color = 'error';
        return <Chip label={params.value} color={color as any} size="small" />;
      }
    },
    {
      field: 'ai_filter',
      headerName: 'AI Filter',
      width: 100,
      type: 'string',
      valueGetter: (params: GridValueGetterParams) => {
        // Use the actual status from the database if available
        const status = params.row.status;
        if (status === 'passed') return 'Passed';
        if (status === 'failed') return 'Failed';
        if (status === 'no_ai') return 'No AI';
        
        // Fallback to calculated status if database status is not available
        const instance = params.row.instance?.toLowerCase();
        const sentiment = params.row.ai_sentiment?.toLowerCase();
        const risk = params.row.ai_risk?.toLowerCase();
        
        // For KnightRider and Ladyjane (longs): Pass if bullish and low/medium risk
        if (instance === 'knightrider' || instance === 'ladyjane') {
          return (sentiment === 'bullish' && (risk === 'low' || risk === 'medium')) ? 'Passed' : 'Failed';
        }
        
        // For Dev and V1 (shorts): Pass if bearish and high/medium risk
        if (instance === 'dev' || instance === 'v1') {
          return (sentiment === 'bearish' && (risk === 'high' || risk === 'medium')) ? 'Passed' : 'Failed';
        }
        
        // Default case: Pass if bullish and low/medium risk
        return (sentiment === 'bullish' && (risk === 'low' || risk === 'medium')) ? 'Passed' : 'Failed';
      },
      renderCell: (params: GridRenderCellParams) => {
        const value = params.value;
        let color = 'default';
        if (value === 'Passed') color = 'success';
        else if (value === 'Failed') color = 'error';
        else if (value === 'No AI') color = 'warning';
        return <Chip label={value} color={color as any} size="small" />;
      }
    }
  ];

  // Generate columns based on data structure
  const generateColumns = (data: any[]): GridColDef[] => {
    if (!data.length) return [];
    
    // Define base columns excluding AI filter fields
    const baseColumns = Object.keys(data[0])
      .filter(key => !['id', 'ai_sentiment', 'ai_risk', 'ai_filter'].includes(key))
      .map(key => {
        const column: GridColDef = {
          field: key,
          headerName: key.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' '),
          width: 150,
          valueFormatter: (params: GridValueFormatterParams) => {
            if (params.value == null) return '';
            
            // Format numbers with commas
            if (typeof params.value === 'number') {
              if (key.includes('price') || key.includes('range')) {
                return `$${params.value.toFixed(2)}`;
              }
              if (key.includes('percent') || key.includes('rate')) {
                return `${params.value.toFixed(2)}%`;
              }
              if (key.includes('volume') || key.includes('dollar')) {
                return params.value.toLocaleString(undefined, { 
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 0
                });
              }
              return params.value.toLocaleString();
            }
            
            return params.value;
          }
        };

        // Make symbol column clickable
        if (key === 'symbol') {
          column.renderCell = (params: GridRenderCellParams) => {
            const symbol = params.value;
            if (!symbol) return params.value;
            
            return (
              <Typography
                variant="body2"
                onClick={() => handleSymbolClick(symbol)}
                sx={{ 
                  color: '#90caf9', 
                  textDecoration: 'none',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  '&:hover': {
                    textDecoration: 'underline'
                  }
                }}
              >
                {symbol}
              </Typography>
            );
          };
        }

        return column;
      });

    return baseColumns;
  };

  const ordersColumns = generateColumns(ordersData);
  
  // Trade pairs columns for clean entry/exit view
  // Get trade pairs columns with optional instrument_type column for HYBRID
  const getTradePairsColumns = (): GridColDef[] => {
    const baseColumns: GridColDef[] = [
      {
        field: 'symbol',
        headerName: 'Symbol',
        width: 100,
        renderCell: (params: GridRenderCellParams) => {
          const symbol = params.value;
          return (
            <Button
              variant="text"
              onClick={() => handleSymbolClick(symbol)}
              sx={{
                minWidth: 'auto',
                padding: '2px 6px',
                fontSize: '0.875rem',
                fontWeight: 'bold',
                color: 'primary.main',
                '&:hover': {
                  backgroundColor: 'primary.light',
                  color: 'white',
                },
              }}
            >
              {symbol}
            </Button>
          );
        },
      },
      {
        field: 'position_type',
        headerName: 'Type',
        width: 80,
        renderCell: (params: GridRenderCellParams) => {
          const type = params.value;
          return (
            <Chip
              label={type}
              size="small"
              color={type === 'LONG' ? 'success' : type === 'SHORT' ? 'error' : 'warning'}
              variant="filled"
              sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}
            />
          );
        },
      },
    ];

    // Add instrument_type column for legacy HYBRID (hybrid system)
    if (instanceName === 'HYBRID') {
      baseColumns.push({
        field: 'instrument_type',
        headerName: 'Instrument',
        width: 100,
        renderCell: (params: GridRenderCellParams) => {
          const type = params.value;
          return (
            <Chip
              label={type}
              size="small"
              color={type === 'OPTION' ? 'info' : 'primary'}
              variant="outlined"
              sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}
            />
          );
        },
      });
    }

    return baseColumns;
  };

  const tradePairsColumns: GridColDef[] = [
    ...getTradePairsColumns(),
    {
      field: 'entry_date',
      headerName: 'Entry Date',
      width: 110,
    },
    {
      field: 'entry_time',
      headerName: 'Entry Time',
      width: 100,
    },
    {
      field: 'exit_date',
      headerName: 'Exit Date',
      width: 110,
    },
    {
      field: 'exit_time',
      headerName: 'Exit Time',
      width: 100,
    },
    {
      field: 'quantity',
      headerName: 'Qty',
      width: 80,
      type: 'number',
      valueFormatter: (params) => {
        const value = params.value;
        if (value === null || value === undefined) return '0';
        // For very small decimal values (crypto), show more precision
        if (Math.abs(value) < 1) {
          return value.toFixed(6);
        }
        // For larger values, show 2 decimal places
        return value.toFixed(2);
      },
    },
    {
      field: 'entry_price',
      headerName: 'Entry Price',
      width: 110,
      type: 'number',
      valueFormatter: (params) => `$${params.value?.toFixed(2) || '0.00'}`,
    },
    {
      field: 'exit_price',
      headerName: 'Exit Price',
      width: 110,
      type: 'number',
      valueFormatter: (params) => `$${params.value?.toFixed(2) || '0.00'}`,
    },
    {
      field: 'pnl_dollars',
      headerName: 'P&L ($)',
      width: 100,
      type: 'number',
      renderCell: (params: GridRenderCellParams) => {
        const value = params.value;
        const isProfit = value > 0;
        return (
          <Typography
            sx={{
              color: isProfit ? 'success.main' : 'error.main',
              fontWeight: 'bold',
            }}
          >
            {isProfit ? '+' : ''}${value?.toFixed(2) || '0.00'}
          </Typography>
        );
      },
    },
    {
      field: 'pnl_percent',
      headerName: 'P&L (%)',
      width: 100,
      type: 'number',
      renderCell: (params: GridRenderCellParams) => {
        const value = params.value ?? 0;
        const isProfit = value > 0;
        // Use more decimal places for small percentages (e.g., futures)
        const absValue = Math.abs(value);
        const decimals = absValue > 0 && absValue < 0.01 ? 4 : 2;
        const formatted = typeof value === 'number' ? value.toFixed(decimals) : '0.00';
        return (
          <Typography
            sx={{
              color: isProfit ? 'success.main' : 'error.main',
              fontWeight: 'bold',
            }}
          >
            {isProfit ? '+' : ''}{formatted}%
          </Typography>
        );
      },
    },
  ];

  // Map internal names to display names
  const getDisplayName = (internalName: string | undefined) => {
    if (!internalName) return '';
    const displayNameMap: { [key: string]: string } = {
      'V1': 'OPTIONS',
      'E_OPTIONS': 'E-OPTIONS',
      'HYBRID': 'HYBRID',
      'OPTIONS_PCS': 'OPTIONS PCS',
      'TOPORB': 'TIER2',
      'TIER3': 'TIER3',
      'LIVE': 'LIVE',
      'FUTURES': 'Futures'
    };
    return displayNameMap[internalName] || internalName;
  };

  return (
    <Box key={name} sx={{ p: { xs: 2, sm: 3 }, maxWidth: '100%', overflow: 'hidden' }}>
      <Box sx={{ 
        mb: 3, 
        p: { xs: 2, sm: 3 },
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        borderRadius: 3,
        boxShadow: 3,
        display: 'flex',
        flexDirection: { xs: 'column', sm: 'row' },
        alignItems: { xs: 'flex-start', sm: 'center' },
        gap: 2 
      }}>
        <Typography variant="h5" component="h1" sx={{ color: 'white', fontWeight: 'bold', flex: 1 }}>
          {getDisplayName(name)}
        </Typography>
        
        {/* Legacy HYBRID Service Controls */}
        {instanceName === 'HYBRID' && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            {/* Status Indicator */}
            {serviceStatus && (
              <Chip
                label={
                  serviceStatus.is_running 
                    ? `Running: ${serviceStatus.current_mode === 'default' ? 'Hybrid' : serviceStatus.current_mode === 'stocks' ? 'Stocks' : 'Options'}`
                    : 'Inactive'
                }
                color={serviceStatus.is_running ? 'success' : 'default'}
                sx={{ 
                  color: 'white', 
                  fontWeight: 'bold',
                  fontSize: serviceStatus.is_running ? '0.8125rem' : '0.95rem',
                  px: serviceStatus.is_running ? 1 : 1.5,
                  py: serviceStatus.is_running ? 0 : 0.25,
                  bgcolor: serviceStatus.is_running ? 'rgba(76, 175, 80, 0.8)' : 'rgba(158, 158, 158, 0.5)'
                }}
              />
            )}
            
            {/* iOS-Style Toggle Switch */}
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.75 }}>
              {/* Toggle Track */}
              <Box
                onClick={() => {
                  if (serviceLoading) return;
                  // Cycle through modes when clicked
                  const modes = ['options', 'default', 'stocks'];
                  const currentIndex = modes.indexOf(serviceMode);
                  const nextMode = modes[(currentIndex + 1) % modes.length];
                  
                  if (serviceStatus?.is_running) {
                    // If running, trigger mode change with restart
                    handleModeChange(null as any, nextMode);
                  } else {
                    // If stopped, just change the mode selection
                    setServiceMode(nextMode);
                  }
                }}
                sx={{
                  position: 'relative',
                  width: '150px',
                  height: '32px',
                  bgcolor: 'rgba(255,255,255,0.2)',
                  borderRadius: '16px',
                  cursor: serviceLoading ? 'not-allowed' : 'pointer',
                  border: '2px solid rgba(255,255,255,0.3)',
                  display: 'flex',
                  alignItems: 'center',
                  transition: 'all 0.3s ease',
                  '&:hover': serviceLoading ? {} : {
                    bgcolor: 'rgba(255,255,255,0.25)',
                    borderColor: 'rgba(255,255,255,0.4)',
                  },
                  opacity: serviceLoading ? 0.6 : 1,
                }}
              >
                {/* Sliding knob */}
                <Box
                  sx={{
                    position: 'absolute',
                    width: '50px',
                    height: '26px',
                    bgcolor: 'white',
                    borderRadius: '13px',
                    boxShadow: '0 2px 6px rgba(0,0,0,0.3)',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    left: serviceMode === 'options' ? '3px' : serviceMode === 'default' ? '50px' : '97px',
                  }}
                />
              </Box>
              
              {/* Labels underneath */}
              <Box sx={{ 
                display: 'flex', 
                width: '150px',
                justifyContent: 'space-between',
                px: 0.5
              }}>
                <Typography sx={{ 
                  fontSize: '0.7rem', 
                  fontWeight: serviceMode === 'options' ? 'bold' : 'normal',
                  color: serviceMode === 'options' ? 'white' : 'rgba(255,255,255,0.6)',
                  transition: 'all 0.3s ease'
                }}>
                  Options
                </Typography>
                <Typography sx={{ 
                  fontSize: '0.7rem', 
                  fontWeight: serviceMode === 'default' ? 'bold' : 'normal',
                  color: serviceMode === 'default' ? 'white' : 'rgba(255,255,255,0.6)',
                  transition: 'all 0.3s ease'
                }}>
                  Hybrid
                </Typography>
                <Typography sx={{ 
                  fontSize: '0.7rem', 
                  fontWeight: serviceMode === 'stocks' ? 'bold' : 'normal',
                  color: serviceMode === 'stocks' ? 'white' : 'rgba(255,255,255,0.6)',
                  transition: 'all 0.3s ease'
                }}>
                  Stocks
                </Typography>
              </Box>
            </Box>
            
            {/* Start Button */}
            <Button
              variant="contained"
              startIcon={serviceLoading ? <CircularProgress size={16} /> : <PlayArrowIcon />}
              onClick={handleStartService}
              disabled={serviceLoading || serviceStatus?.is_running}
              sx={{
                bgcolor: 'rgba(76, 175, 80, 0.9)',
                '&:hover': { bgcolor: 'rgba(76, 175, 80, 1)' },
                '&:disabled': { bgcolor: 'rgba(158, 158, 158, 0.3)' },
                color: 'white',
                fontWeight: 'bold',
                textTransform: 'none'
              }}
            >
              Start
            </Button>
            
            {/* Restart Button */}
            <Button
              variant="contained"
              startIcon={<RestartAltIcon />}
              onClick={handleRestartService}
              disabled={serviceLoading || !serviceStatus?.is_running}
              sx={{
                bgcolor: 'rgba(255, 152, 0, 0.9)',
                '&:hover': { bgcolor: 'rgba(255, 152, 0, 1)' },
                '&:disabled': { bgcolor: 'rgba(158, 158, 158, 0.3)' },
                color: 'white',
                fontWeight: 'bold',
                textTransform: 'none'
              }}
            >
              Restart
            </Button>
            
            {/* Stop Button */}
            <Button
              variant="contained"
              startIcon={<StopIcon />}
              onClick={() => setStopConfirmOpen(true)}
              disabled={serviceLoading || !serviceStatus?.is_running}
              sx={{
                bgcolor: 'rgba(244, 67, 54, 0.9)',
                '&:hover': { bgcolor: 'rgba(244, 67, 54, 1)' },
                '&:disabled': { bgcolor: 'rgba(158, 158, 158, 0.3)' },
                color: 'white',
                fontWeight: 'bold',
                textTransform: 'none'
              }}
            >
              Stop
            </Button>
          </Box>
        )}
        
        {isRefreshingFromImport && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CircularProgress size={20} sx={{ color: 'white' }} />
            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)' }}>
              Refreshing from import...
            </Typography>
          </Box>
        )}
      </Box>
      
      {/* Stop Confirmation Dialog */}
      <Dialog
        open={stopConfirmOpen}
        onClose={() => setStopConfirmOpen(false)}
      >
        <DialogTitle>Stop Service</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to stop the trading service? This will close any open positions and stop the system from trading.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStopConfirmOpen(false)} color="primary">
            Cancel
          </Button>
          <Button onClick={handleStopService} color="error" variant="contained" autoFocus>
            Stop Service
          </Button>
        </DialogActions>
      </Dialog>

      {/* App Status Indicator, Timing Controls, and RUN/STOP buttons removed per request */}

      {/* RUN APP Result Display */}
      {runAppResult && (
        <Alert 
          severity={runAppResult.status === 'success' ? 'success' : 'error'}
          sx={{ mb: 2 }}
        >
          <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
            {runAppResult.message}
          </Typography>
          {runAppResult.output && (
            <Box sx={{ mt: 1, p: 1, backgroundColor: 'rgba(0,0,0,0.1)', borderRadius: 1 }}>
              <Typography variant="body2" component="pre" sx={{ 
                margin: 0, 
                whiteSpace: 'pre-wrap', 
                fontSize: '12px',
                fontFamily: 'monospace'
              }}>
                {runAppResult.output}
              </Typography>
            </Box>
          )}
          {runAppResult.error && (
            <Box sx={{ mt: 1, p: 1, backgroundColor: 'rgba(255,0,0,0.1)', borderRadius: 1 }}>
              <Typography variant="body2" component="pre" sx={{ 
                margin: 0, 
                whiteSpace: 'pre-wrap', 
                fontSize: '12px',
                fontFamily: 'monospace',
                color: 'error.main'
              }}>
                {runAppResult.error}
              </Typography>
            </Box>
          )}
        </Alert>
      )}

      {/* STOP APP Result Display */}
      {stopAppResult && (
        <Alert 
          severity={stopAppResult.status === 'success' ? 'success' : stopAppResult.status === 'warning' ? 'warning' : 'error'}
          sx={{ mb: 2 }}
        >
          <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
            {stopAppResult.message}
          </Typography>
          {stopAppResult.output && (
            <Box sx={{ mt: 1, p: 1, backgroundColor: 'rgba(0,0,0,0.1)', borderRadius: 1 }}>
              <Typography variant="body2" component="pre" sx={{ 
                margin: 0, 
                whiteSpace: 'pre-wrap', 
                fontSize: '12px',
                fontFamily: 'monospace'
              }}>
                {stopAppResult.output}
              </Typography>
            </Box>
          )}
          {stopAppResult.error && (
            <Box sx={{ mt: 1, p: 1, backgroundColor: 'rgba(255,0,0,0.1)', borderRadius: 1 }}>
              <Typography variant="body2" component="pre" sx={{ 
                margin: 0, 
                whiteSpace: 'pre-wrap', 
                fontSize: '12px',
                fontFamily: 'monospace',
                color: 'error.main'
              }}>
                {stopAppResult.error}
              </Typography>
            </Box>
          )}
          {stopAppResult.remaining_processes && stopAppResult.remaining_processes.length > 0 && (
            <Box sx={{ mt: 1, p: 1, backgroundColor: 'rgba(255,165,0,0.1)', borderRadius: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'warning.main' }}>
                Remaining processes:
              </Typography>
              <Typography variant="body2" component="pre" sx={{ 
                margin: 0, 
                whiteSpace: 'pre-wrap', 
                fontSize: '12px',
                fontFamily: 'monospace',
                color: 'warning.main'
              }}>
                {stopAppResult.remaining_processes.join('\n')}
              </Typography>
            </Box>
          )}
        </Alert>
      )}

            <Paper sx={{ width: '100%', mb: 2 }}>
        <Tabs 
          value={activeTab} 
          onChange={handleChange} 
          aria-label="instance tabs"
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            borderBottom: 1,
            borderColor: 'divider',
            '& .MuiTab-root': {
              minWidth: { xs: 'auto', sm: 160 },
              fontWeight: 600,
              textTransform: 'none',
              fontSize: { xs: '0.875rem', sm: '1rem' }
            }
          }}
        >
          {(instanceName === 'V1' || instanceName === 'E_OPTIONS' || instanceName === 'OPTIONS_PCS' || instanceName === 'HYBRID' || instanceName === 'TOPORB' || instanceName === 'TIER3' || instanceName === 'LIVE' || instanceName === 'ORB_AVI' || instanceName === 'ORB_JOHN' || instanceName === 'GROK' || instanceName === 'FUTURES') && (
            <Tab label="Monitoring" id="simple-tab-0" aria-controls="simple-tabpanel-0" />
          )}
          {instanceName === 'LADYJANE' && (
            <Tab label="Monitor" id="simple-tab-0" aria-controls="simple-tabpanel-0" />
          )}
          {/* Orders - for V1 this tab moves to just before Logs (10/6 operator
              request; the prompt's recommended order is Overview · Trade log ·
              Gamma map · Shadows · anything else) */}
          {instanceName !== 'V1' && (
            <Tab label="Orders" id={`simple-tab-${(instanceName === 'E_OPTIONS' || instanceName === 'OPTIONS_PCS' || instanceName === 'HYBRID' || instanceName === 'TOPORB' || instanceName === 'TIER3' || instanceName === 'LIVE' || instanceName === 'LADYJANE' || instanceName === 'ORB_AVI' || instanceName === 'ORB_JOHN' || instanceName === 'GROK' || instanceName === 'FUTURES') ? 1 : 0}`} aria-controls={`simple-tabpanel-${(instanceName === 'E_OPTIONS' || instanceName === 'OPTIONS_PCS' || instanceName === 'HYBRID' || instanceName === 'TOPORB' || instanceName === 'TIER3' || instanceName === 'LIVE' || instanceName === 'LADYJANE' || instanceName === 'ORB_AVI' || instanceName === 'ORB_JOHN' || instanceName === 'GROK' || instanceName === 'FUTURES') ? 1 : 0}`} />
          )}
          {(instanceName === 'V1' || instanceName === 'E_OPTIONS' || instanceName === 'OPTIONS_PCS' || instanceName === 'HYBRID' || instanceName === 'TOPORB' || instanceName === 'TIER3' || instanceName === 'LIVE' || instanceName === 'ORB_AVI' || instanceName === 'ORB_JOHN' || instanceName === 'GROK' || instanceName === 'FUTURES') && (
            <Tab label="EOD" id="simple-tab-2" aria-controls="simple-tabpanel-2" />
          )}
          {/* Live Trade tab - V1 only, right after EOD, before Gamma Map
              (operator placement, 10/6): the v0.47.0 live trade tracker */}
          {instanceName === 'V1' && (
            <Tab label="Live Trade" id="simple-tab-live-trade" aria-controls="simple-tabpanel-live-trade" />
          )}
          {/* Charts tab - Available for ALL instances (V1 shows the gamma map) */}
            <Tab label={instanceName === 'V1' ? 'Gamma Map' : 'Charts'} id="simple-tab-3" aria-controls="simple-tabpanel-3" />
          {/* Shadows tab - V1 (OPT-SPY-DAILY) only, immediately after the gamma map.
              Its presence shifts the Logs tab to index 5 for V1. */}
          {instanceName === 'V1' && (
            <Tab label="Shadows" id="simple-tab-shadows" aria-controls="simple-tabpanel-shadows" />
          )}
          {/* V1 Orders tab - after Shadows, before Logs (index 4) */}
          {instanceName === 'V1' && (
            <Tab label="Orders" id="simple-tab-orders-v1" aria-controls="simple-tabpanel-orders-v1" />
          )}
          {/* Logs tab - Admin only, Available for ALL instances */}
          {isAdmin && (
            <Tab label="Logs" id="simple-tab-4" aria-controls="simple-tabpanel-4" />
          )}
        </Tabs>
      </Paper>

      {/* V1/E_OPTIONS/OPTIONS_PCS/HYBRID ORB-Style Monitoring - default tab index 0 */}
      {(instanceName === 'V1' || instanceName === 'E_OPTIONS' || instanceName === 'OPTIONS_PCS' || instanceName === 'HYBRID' || instanceName === 'TOPORB' || instanceName === 'TIER3' || instanceName === 'LIVE' || instanceName === 'ORB_AVI' || instanceName === 'ORB_JOHN' || instanceName === 'GROK' || instanceName === 'FUTURES') && (
        <TabPanel value={activeTab} index={0}>
          <ORBMonitoring instanceName={instanceName} />
        </TabPanel>
      )}

      {/* Ladyjane VWAP Monitoring */}
      {instanceName === 'LADYJANE' && (
        <TabPanel value={activeTab} index={0}>
          <V1VWAPMonitoring instanceName={instanceName} />
        </TabPanel>
      )}

      

      {/* Charts Tab - Available for ALL instances, Tab index 3.
          V1 (OPTIONS) shows the OPT-SPY-DAILY gamma map instead of trade charts. */}
        <TabPanel value={activeTab} index={3}>
          {instanceName === 'V1' ? (
            <GexMapView instanceName={instanceName} />
          ) : (
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom sx={{ color: '#1976d2', fontWeight: 'bold', mb: 2 }}>
              📊 Trade Charts - Visualize Your Trades
            </Typography>
            
            {/* Chart Controls */}
            <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap', alignItems: 'center' }}>
              {/* Date Picker */}
              <TextField
                label="Date"
                type="date"
                value={chartDate}
                onChange={(e) => {
                  setChartDate(e.target.value);
                  setChartSymbol(''); // Reset symbol when date changes
                  setChartData(null);
                  // Fetch available symbols for this date
                  if (e.target.value) {
                    fetchAvailableSymbols(e.target.value);
                  }
                }}
                InputLabelProps={{ shrink: true }}
                size="small"
                sx={{ minWidth: 200 }}
              />
              
              {/* Symbol Dropdown */}
              <FormControl size="small" sx={{ minWidth: 200 }}>
                <InputLabel id="symbol-select-label">Symbol</InputLabel>
                <Select
                  labelId="symbol-select-label"
                  id="symbol-select"
                  value={chartSymbol || ''}
                  label="Symbol"
                  onChange={(e) => {
                    const newValue = e.target.value as string;
                    setChartSymbol(newValue);
                  }}
                  disabled={!chartDate || availableSymbols.length === 0}
                >
                  {availableSymbols.map((symbol) => (
                    <MenuItem key={symbol} value={symbol}>
                      {symbol}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              
              {/* Timeframe Selector */}
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel>Timeframe</InputLabel>
                <Select
                  value={chartTimeframe}
                  label="Timeframe"
                  onChange={(e) => setChartTimeframe(e.target.value)}
                >
                  <MenuItem value="1">1 min</MenuItem>
                  <MenuItem value="5">5 min</MenuItem>
                  <MenuItem value="15">15 min (default)</MenuItem>
                  <MenuItem value="60">1 hour</MenuItem>
                  <MenuItem value="D">Daily</MenuItem>
                </Select>
              </FormControl>
              
              {/* Load Chart Button */}
              <Button
                variant="contained"
                onClick={loadChartData}
                disabled={!chartDate || !chartSymbol || chartLoading}
                startIcon={chartLoading ? <CircularProgress size={16} /> : null}
                sx={{
                  bgcolor: 'rgba(25, 118, 210, 0.9)',
                  '&:hover': { bgcolor: 'rgba(25, 118, 210, 1)' },
                  color: 'white',
                  fontWeight: 'bold',
                  textTransform: 'none'
                }}
              >
                {chartLoading ? 'Loading...' : 'Load Chart'}
              </Button>
            </Box>

            {/* Error Display */}
            {chartError && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setChartError(null)}>
                {chartError}
              </Alert>
            )}

            {/* Chart Display or Empty State */}
            {!chartData ? (
              <Box sx={{ 
                display: 'flex', 
                flexDirection: 'column', 
                alignItems: 'center', 
                justifyContent: 'center',
                minHeight: '400px',
                bgcolor: 'rgba(0,0,0,0.02)',
                borderRadius: 2,
                border: '2px dashed rgba(0,0,0,0.1)'
              }}>
                <Typography variant="h5" sx={{ color: 'text.secondary', mb: 2 }}>
                  📈 No Chart Selected
                </Typography>
                <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                  Select a date and symbol above, then click "Load Chart"
                </Typography>
              </Box>
            ) : (
              <>
                {/* Chart Container */}
                <Box
                  ref={chartContainerRef}
                  sx={{
                    width: '100%',
                    height: '600px',
                    bgcolor: 'white',
                    borderRadius: 1,
                    border: '1px solid rgba(0,0,0,0.1)',
                    mb: 3
                  }}
                />
              </>
            )}
          </Paper>
          )}
        </TabPanel>

      {/* Orders/Account Summary - Tab index 1 for instances with a Monitoring tab, 0 for others */}
      <TabPanel value={activeTab} index={instanceName === 'V1' ? 5 : (instanceName === 'HYBRID' || instanceName === 'E_OPTIONS' || instanceName === 'OPTIONS_PCS' || instanceName === 'TOPORB' || instanceName === 'TIER3' || instanceName === 'LIVE' || instanceName === 'ORB_AVI' || instanceName === 'ORB_JOHN' || instanceName === 'GROK' || instanceName === 'LADYJANE' || instanceName === 'FUTURES') ? 1 : 0}>
        {/* Account Summary Section */}
        <Paper sx={{ p: 2, mb: 3 }}>
          <Typography variant="h6" gutterBottom sx={{ color: '#1976d2', fontWeight: 'bold' }}>
            Account Summary - {getDisplayName(instanceName)}
          </Typography>
          
          {accountSummaryLoading && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={20} />
              <Typography variant="body2">Loading account data...</Typography>
            </Box>
          )}
          
          {accountSummaryError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {accountSummaryError}
            </Alert>
          )}
          
          {accountSummary && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="h6" sx={{ 
                color: accountSummary.daily_pl >= 0 ? '#2e7d32' : '#d32f2f',
                fontWeight: 'bold',
                fontSize: '1.2rem'
              }}>
                Daily P&L: {accountSummary.daily_pl >= 0 ? '+' : ''}${accountSummary.daily_pl.toFixed(2)} 
                ({accountSummary.daily_pl_percent >= 0 ? '+' : ''}{accountSummary.daily_pl_percent.toFixed(2)}%)
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Account Equity: ${accountSummary.equity.toFixed(2)} | 
                Buying Power: ${accountSummary.buying_power.toFixed(2)}
              </Typography>
            </Box>
          )}
          
          {/* Positions Summary */}
          <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
            Current Positions ({positionsData.length}):
          </Typography>
          
          {positionsLoading && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={20} />
              <Typography variant="body2">Loading positions...</Typography>
            </Box>
          )}
          
          {positionsError && (
            <Alert severity="error" sx={{ mb: 1 }}>
              {positionsError}
            </Alert>
          )}
          
          {positionsData.length === 0 && !positionsLoading && !positionsError && (
            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
              No open positions
            </Typography>
          )}
          
          {positionsData.length > 0 && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {positionsData.map((position, index) => (
                <Box key={index} sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 2,
                  p: 1,
                  borderRadius: 1,
                  border: '1px solid rgba(255, 255, 255, 0.12)'
                }}>
                  <Chip 
                    label={position.symbol} 
                    color="primary" 
                    size="small"
                    onClick={() => handleSymbolClick(position.symbol)}
                    sx={{ cursor: 'pointer', minWidth: 60 }}
                  />
                  <Chip 
                    label={getPositionSideLabel(position.symbol, position.side)} 
                    color={position.side === 'long' ? 'success' : 'error'} 
                    size="small"
                    sx={{ minWidth: 60 }}
                  />
                  {instanceName === 'E_OPTIONS' ? (
                    // E_OPTIONS is the only instance that holds positions across multiple
                    // days, so show BOTH today's intraday P&L and the cumulative P&L
                    // since the position was opened.
                    <Box sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      minWidth: 200,
                      lineHeight: 1.2,
                    }}>
                      <Typography variant="body2" sx={{
                        color: position.unrealized_intraday_pl >= 0 ? '#2e7d32' : '#d32f2f',
                        fontWeight: 'bold',
                        fontSize: '0.8rem',
                      }}>
                        Today:&nbsp;
                        {position.unrealized_intraday_pl >= 0 ? '+' : ''}${position.unrealized_intraday_pl.toFixed(2)}
                        &nbsp;({position.unrealized_intraday_plpc >= 0 ? '+' : ''}{(position.unrealized_intraday_plpc * 100).toFixed(2)}%)
                      </Typography>
                      <Typography variant="body2" sx={{
                        color: position.unrealized_pl >= 0 ? '#2e7d32' : '#d32f2f',
                        fontWeight: 'bold',
                        fontSize: '0.8rem',
                      }}>
                        Since open:&nbsp;
                        {position.unrealized_pl >= 0 ? '+' : ''}${position.unrealized_pl.toFixed(2)}
                        &nbsp;({position.unrealized_plpc >= 0 ? '+' : ''}{(position.unrealized_plpc * 100).toFixed(2)}%)
                      </Typography>
                    </Box>
                  ) : (
                    <Typography variant="body2" sx={{ 
                      color: position.unrealized_pl >= 0 ? '#2e7d32' : '#d32f2f',
                      fontWeight: 'bold',
                      minWidth: 100
                    }}>
                      {position.unrealized_pl >= 0 ? '+' : ''}${position.unrealized_pl.toFixed(2)} 
                      ({position.unrealized_plpc >= 0 ? '+' : ''}{(position.unrealized_plpc * 100).toFixed(2)}%)
                    </Typography>
                  )}
                  <Typography variant="body2" color="text.secondary">
                    {position.quantity} shares @ ${position.current_price.toFixed(2)}
                  </Typography>
                  {position.entry_time && (
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                      Entry: {position.entry_time}
                    </Typography>
                  )}
                  {position.stop_loss_percent !== null && (
                    <Typography variant="body2" sx={{ 
                      fontSize: '0.75rem',
                      color: '#ff9800',
                      fontWeight: 'bold'
                    }}>
                      Stop: -{position.stop_loss_percent.toFixed(2)}%
                      {position.stop_loss_price && (
                        <span style={{ color: '#ffa726' }}>
                          {' @ $'}{position.stop_loss_price.toFixed(2)}
                        </span>
                      )}
                    </Typography>
                  )}
                </Box>
              ))}
            </Box>
          )}
        </Paper>

        <Box sx={{ mb: 2 }}>
          <Typography variant="h6" gutterBottom>
            Completed Trades - {tradePairsData.length} Trade Pairs
          </Typography>
          
          <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            <TextField
              label="Start Date"
              type="date"
              value={ordersFilters.start_date}
              onChange={(e) => {
                const v = e.target.value;
                const end = ordersFilters.end_date;
                const newEnd = end && end < v ? v : end;
                handleOrdersFiltersChange({ ...ordersFilters, start_date: v, end_date: newEnd });
              }}
              size="small"
              sx={{ width: 160 }}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="End Date"
              type="date"
              value={ordersFilters.end_date}
              onChange={(e) => {
                const v = e.target.value;
                const start = ordersFilters.start_date;
                const newStart = start && start > v ? v : start;
                handleOrdersFiltersChange({ ...ordersFilters, start_date: newStart, end_date: v });
              }}
              size="small"
              sx={{ width: 160 }}
              InputLabelProps={{ shrink: true }}
            />
            <Button 
              variant="contained" 
              onClick={() => {
                fetchTradePairsData();
                fetchPositionsData();
                fetchAccountSummary();
              }}
              disabled={tradePairsLoading || positionsLoading || accountSummaryLoading}
            >
              {(tradePairsLoading || positionsLoading || accountSummaryLoading) ? <CircularProgress size={20} /> : 'Refresh All'}
            </Button>
          </Box>

          {/* Per-Instrument-Type Summary for legacy HYBRID */}
          {instanceName === 'HYBRID' && tradePairsResponse?.per_instrument_type && (
            <Paper sx={{ p: 2, mb: 2, background: 'linear-gradient(135deg, #667eea22 0%, #764ba222 100%)' }}>
              <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold', mb: 1.5 }}>
                📊 Instrument Type Breakdown
              </Typography>
              <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                <Box>
                  <Chip label="EQUITY" color="primary" size="small" sx={{ mb: 0.5 }} />
                  <Typography variant="body2">
                    <strong>{tradePairsResponse.per_instrument_type.EQUITY.trades}</strong> trades • 
                    <strong style={{ color: tradePairsResponse.per_instrument_type.EQUITY.realized_pnl >= 0 ? '#4caf50' : '#f44336' }}>
                      {' '}${tradePairsResponse.per_instrument_type.EQUITY.realized_pnl.toFixed(2)}
                    </strong> P&L • 
                    <strong> {tradePairsResponse.per_instrument_type.EQUITY.unique_symbols}</strong> symbols
                  </Typography>
                </Box>
                <Box>
                  <Chip label="OPTION" color="info" size="small" sx={{ mb: 0.5 }} />
                  <Typography variant="body2">
                    <strong>{tradePairsResponse.per_instrument_type.OPTION.trades}</strong> trades • 
                    <strong style={{ color: tradePairsResponse.per_instrument_type.OPTION.realized_pnl >= 0 ? '#4caf50' : '#f44336' }}>
                      {' '}${tradePairsResponse.per_instrument_type.OPTION.realized_pnl.toFixed(2)}
                    </strong> P&L • 
                    <strong> {tradePairsResponse.per_instrument_type.OPTION.unique_symbols}</strong> symbols
                  </Typography>
                </Box>
              </Box>
            </Paper>
          )}

          {tradePairsError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {tradePairsError}
            </Alert>
          )}

          {tradePairsLoading && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <CircularProgress size={20} />
              <Typography variant="body2">Loading trade pairs...</Typography>
            </Box>
          )}

          {!tradePairsLoading && tradePairsData.length === 0 && (
            <Alert severity="info" sx={{ mb: 2 }}>
              No completed trade pairs found in the selected date range. Try expanding the date range to see trades where entry and exit occurred in different periods.
            </Alert>
          )}
        </Box>

        <DataGrid
          rows={tradePairsData.map((trade, index) => ({ ...trade, id: index }))}
          columns={tradePairsColumns}
          autoHeight
          loading={tradePairsLoading}
          initialState={{
            pagination: {
              paginationModel: { pageSize: 25, page: 0 },
            },
            sorting: {
              sortModel: [{ field: 'exit_date', sort: 'desc' }],
            },
          }}
          pageSizeOptions={[10, 25, 50]}
          disableRowSelectionOnClick
        />
      </TabPanel>

      {/* EOD Tab - index 1 on V1 (Orders moved to just before Logs, 10/6),
          index 2 for the other instances with a Monitoring tab */}
      {(instanceName === 'V1' || instanceName === 'E_OPTIONS' || instanceName === 'OPTIONS_PCS' || instanceName === 'HYBRID' || instanceName === 'TOPORB' || instanceName === 'TIER3' || instanceName === 'LIVE' || instanceName === 'ORB_AVI' || instanceName === 'ORB_JOHN' || instanceName === 'GROK' || instanceName === 'FUTURES') && (
        <TabPanel value={activeTab} index={instanceName === 'V1' ? 1 : 2}>
          {instanceName === 'V1' ? (
            /* V1 runs OPT-SPY-DAILY only — the Account EOD feed was removed 2026-08-26 */
            <OptSpyDailyView instanceName={instanceName} />
          ) : (
            <EODView instanceName={instanceName} />
          )}
        </TabPanel>
      )}

      {/* Live Trade Tab - V1 only, after EOD / before Gamma Map (index 2) */}
      {instanceName === 'V1' && (
        <TabPanel value={activeTab} index={2}>
          <LiveTradeTracker instanceName={instanceName} />
        </TabPanel>
      )}

      {/* Shadows Tab - V1 only, right after the Gamma Map tab (index 4) */}
      {instanceName === 'V1' && (
        <TabPanel value={activeTab} index={4}>
          <ShadowsView instanceName={instanceName} />
        </TabPanel>
      )}

      {/* Logs Tab - Admin only, Available for ALL instances.
          Index 6 on V1 (Live Trade + Shadows + moved Orders shift it),
          4 everywhere else. */}
      {isAdmin && (
        <TabPanel value={activeTab} index={instanceName === 'V1' ? 6 : 4}>
          <Paper sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6" sx={{ color: '#1976d2', fontWeight: 'bold' }}>
                📜 Real-Time Logs - {getDisplayName(instanceName)} {instanceName === 'HYBRID' ? '(systemd journal)' : '(orb_trading.log)'}
              </Typography>
              
              <Box sx={{ display: 'flex', gap: 1 }}>
                {/* Pause/Resume Button */}
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => setIsLogPaused(!isLogPaused)}
                  sx={{ textTransform: 'none' }}
                >
                  {isLogPaused ? '▶️ Resume' : '⏸️ Pause'}
                </Button>
                
                {/* Auto-scroll Toggle */}
                <FormControlLabel
                  control={
                    <Switch
                      checked={autoScroll}
                      onChange={(e) => setAutoScroll(e.target.checked)}
                      size="small"
                    />
                  }
                  label="Auto-scroll"
                  sx={{ ml: 1 }}
                />
                
                {/* Clear Button */}
                <Button
                  variant="outlined"
                  size="small"
                  color="error"
                  onClick={() => setLogLines([])}
                  sx={{ textTransform: 'none' }}
                >
                  🗑️ Clear
                </Button>
              </Box>
            </Box>

            {/* Connection Status */}
            {logError && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                {logError}
              </Alert>
            )}

            {isLogPaused && (
              <Alert severity="info" sx={{ mb: 2 }}>
                ⏸️ Log stream is paused. Click Resume to continue.
              </Alert>
            )}

            {/* Terminal-like Log Display */}
            <Box
              ref={logContainerRef}
              sx={{
                bgcolor: '#1e1e1e',
                color: '#d4d4d4',
                fontFamily: '"Consolas", "Monaco", "Courier New", monospace',
                fontSize: '0.85rem',
                p: 2,
                height: '600px',
                overflowY: 'auto',
                overflowX: 'auto',
                borderRadius: 1,
                border: '1px solid rgba(255,255,255,0.1)',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-all',
                '&::-webkit-scrollbar': {
                  width: '10px',
                  height: '10px',
                },
                '&::-webkit-scrollbar-track': {
                  bgcolor: '#2b2b2b',
                },
                '&::-webkit-scrollbar-thumb': {
                  bgcolor: '#555',
                  borderRadius: '5px',
                  '&:hover': {
                    bgcolor: '#777',
                  },
                },
              }}
            >
              {logLines.length === 0 ? (
                <Typography sx={{ color: '#888', fontStyle: 'italic' }}>
                  Waiting for logs... (Last 100 lines will be loaded)
                </Typography>
              ) : (
                logLines.map((line, index) => (
                  <Box
                    key={index}
                    sx={{
                      py: 0.25,
                      borderBottom: index < logLines.length - 1 ? '1px solid rgba(255,255,255,0.05)' : 'none',
                    }}
                  >
                    {line}
                  </Box>
                ))
              )}
            </Box>

            {/* Stats */}
            <Box sx={{ mt: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="caption" color="text.secondary">
                Displaying last {logLines.length} lines (max 500)
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {isLogPaused ? '⏸️ Paused' : '🔴 Live'}
              </Typography>
            </Box>
          </Paper>
        </TabPanel>
      )}


      {/* VWAP Active Tab removed - no longer in use */}

      {/* Chart Modal */}
      <ChartModal
        open={chartModalOpen}
        onClose={handleCloseChartModal}
        symbol={selectedSymbol}
      />
    </Box>
  );
};

export default InstanceView; 